import { useState } from 'react';
import { Button, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  APP_EVENTS,
  captureAppEvent,
  REPORT_STEP,
  type ReportStep,
} from 'src/services/posthogEvents';
import Sentry from 'src/services/sentry';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import OrderTaskConfirm from 'src/components/ReportViewer/OrderTasks/OrderTaskConfirm';

import { Api } from 'src/models/Api.model';
import { OrderTaskId } from 'src/models/GalenData/Order.model';
import { I18NextNS } from 'src/models/Language.model';

const REPORT_STEP_BY_TASK_ID: Record<OrderTaskId, ReportStep> = {
  IsSharedWithProvider: REPORT_STEP.SHARED_WITH_PROVIDER,
  IsSavedToChart: REPORT_STEP.SAVED_TO_CHART,
};

interface OrderTaskProps {
  label: string;
  isComplete: boolean;
  disabled?: boolean;
  taskId: OrderTaskId;
}

const OrderTask = ({
  label,
  isComplete,
  disabled = false,
  taskId,
}: OrderTaskProps) => {
  const { t } = useTranslation(I18NextNS.COMMON);

  const [showConfirm, setShowConfirm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const { session, setSession } = useRemoteBoundStore();

  const { iid: sessionId, orderId } = session;

  const handleClick = () => {
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    try {
      setIsProcessing(true);

      if (!orderId) {
        Sentry.captureException(new Error(t('message.error.orderNotFound')));
        return;
      }

      await Api.remote.update_exam_order_task(orderId, taskId, true);

      const updatedSession = await Api.remote.load_session({
        deviceDataId: sessionId,
      });

      setSession(updatedSession);

      captureAppEvent(APP_EVENTS.REPORT_STEP_COMPLETED, {
        order_id: orderId,
        step: REPORT_STEP_BY_TASK_ID[taskId],
      });

      setShowConfirm(false);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    setShowConfirm(false);
  };

  return (
    <Stack>
      <div className="d-flex align-items-start">
        <span className="fw-bold" data-test={`order-task-${taskId}-label`}>
          {label}
        </span>
      </div>
      <p
        className="text-muted fs-6"
        data-test={`order-task-${taskId}-instructions`}
      >
        {t('orderTasks.taskinstructions')}
      </p>
      {isComplete ? (
        <div className="d-flex align-items-start">
          <FontAwesomeIcon
            icon={faCheck}
            className="text-success me-2"
            size="lg"
          />
          <p
            className="text-success fw-bold"
            data-test={`order-task-${taskId}-completed`}
          >
            {t('orderTasks.taskCompleted')}
          </p>
        </div>
      ) : (
        <Button
          variant="primary"
          onClick={handleClick}
          disabled={disabled}
          className="align-self-start px-3 py-2 fs-6"
          data-test={`order-task-${taskId}-mark-complete-button`}
          data-track="results.order-task.mark-complete"
          data-observe
        >
          {t('orderTasks.markComplete')}
        </Button>
      )}
      <OrderTaskConfirm
        shouldShowConfirm={showConfirm}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        isProcessing={isProcessing}
      />
    </Stack>
  );
};

export default OrderTask;
